import React, { useState, useEffect, useRef, useMemo } from "react";
import { Globe, Search, ChevronDown } from "lucide-react";
import { TIMEZONE_OPTIONS } from "./timezoneUtils";

const VARIANT_STYLES = {
  google: {
    focusBorder: "focus:border-[#4285F4]/50",
    focusRing: "focus:ring-[#4285F4]/30",
    selectedRow: "bg-[#4285F4]/10 text-[#4285F4]",
    badge: "text-[#4285F4] bg-[#4285F4]/10",
    badgeMuted: "text-[#4285F4]/70",
    hintDetected: "text-[#4285F4]/70",
  },
  ghl: {
    focusBorder: "focus:border-[#01B47A]/50",
    focusRing: "focus:ring-[#01B47A]/30",
    selectedRow: "bg-[#01B47A]/10 text-[#01B47A]",
    badge: "text-[#01B47A] bg-[#01B47A]/10",
    badgeMuted: "text-[#01B47A]/70",
    hintDetected: "text-[#01B47A]/70",
  },
};

/**
 * Searchable timezone dropdown (matches Google Calendar integration styling).
 * @param {string} value — IANA timezone id
 * @param {(tz: string) => void} onChange
 * @param {'google'|'ghl'} variant — accent color theme
 * @param {string} [detectedTimezone] — browser timezone for badge / hint
 * @param {string} [footerHintWhenDetected] — shown when value matches detected
 * @param {string} [footerHintDefault] — shown otherwise
 * @param {string} [label] — field label text
 * @param {boolean} [required] — show required asterisk on label
 * @param {string} [testIdPrefix] — prefix for data-testid attributes
 */
const TimezoneField = ({
  value,
  onChange,
  variant = "google",
  detectedTimezone = "",
  footerHintWhenDetected,
  footerHintDefault,
  label = "Time Zone",
  required = true,
  testIdPrefix = "timezone",
}) => {
  const [timezoneSearch, setTimezoneSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const s = VARIANT_STYLES[variant] || VARIANT_STYLES.google;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setTimezoneSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredTimezones = useMemo(() => {
    if (!timezoneSearch.trim()) return TIMEZONE_OPTIONS;
    const query = timezoneSearch.toLowerCase();
    return TIMEZONE_OPTIONS.filter(
      (tz) =>
        tz.value.toLowerCase().includes(query) ||
        tz.label.toLowerCase().includes(query)
    );
  }, [timezoneSearch]);

  const selectedLabel = useMemo(() => {
    const found = TIMEZONE_OPTIONS.find((tz) => tz.value === value);
    return found ? found.label : value || "";
  }, [value]);

  const triggerDisplay = value ? selectedLabel : "Select a time zone";

  const defaultWhenDetected =
    footerHintWhenDetected ||
    "Automatically detected from your location";
  const defaultOther =
    footerHintDefault ||
    "Select the time zone used for scheduling and webhooks";

  return (
    <div className="space-y-2">
      <label className="block text-[#F3F4F6] text-sm font-body">
        {label}
        {required && <span className="text-red-400"> *</span>}
      </label>
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => {
            setIsOpen(!isOpen);
            setTimezoneSearch("");
          }}
          className={`w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-10 py-3 text-sm font-body text-left focus:outline-none ${s.focusBorder} focus:ring-1 ${s.focusRing} transition-colors`}
          data-testid={`${testIdPrefix}-select`}
        >
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <Globe className="w-4 h-4 text-[#9CA3AF]" />
          </div>
          <span
            className="flex items-center gap-2"
            style={{ color: value ? "#F3F4F6" : "#9CA3AF" }}
          >
            {triggerDisplay}
            {value && value === detectedTimezone && (
              <span className={`text-xs ${s.badge} px-2 py-0.5 rounded-full`}>
                Auto-detected
              </span>
            )}
          </span>
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <ChevronDown
              className={`w-4 h-4 text-[#9CA3AF] transition-transform ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          </div>
        </button>

        {isOpen && (
          <div className="absolute z-50 mt-1 w-full bg-[#1A1D23] border border-white/10 rounded-lg shadow-2xl overflow-hidden">
            <div className="p-2 border-b border-white/10">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                <input
                  type="text"
                  placeholder="Search timezone..."
                  value={timezoneSearch}
                  onChange={(e) => setTimezoneSearch(e.target.value)}
                  className={`w-full bg-white/5 border border-white/10 rounded-md pl-9 pr-3 py-2 text-sm font-body text-[#F3F4F6] placeholder-[#9CA3AF]/60 focus:outline-none ${s.focusBorder}`}
                  autoFocus
                  data-testid={`${testIdPrefix}-search-input`}
                />
              </div>
            </div>

            <div className="max-h-60 overflow-y-auto">
              {filteredTimezones.length === 0 ? (
                <div className="px-4 py-3 text-sm text-[#9CA3AF] text-center">
                  No timezones found
                </div>
              ) : (
                filteredTimezones.map((tz) => (
                  <button
                    key={tz.value}
                    type="button"
                    onClick={() => {
                      onChange(tz.value);
                      setIsOpen(false);
                      setTimezoneSearch("");
                    }}
                    className={`w-full text-left px-4 py-2.5 text-sm font-body transition-colors flex items-center justify-between ${
                      value === tz.value
                        ? s.selectedRow
                        : "text-[#F3F4F6] hover:bg-white/5"
                    }`}
                  >
                    <span>{tz.label}</span>
                    {tz.value === detectedTimezone && (
                      <span className={`text-xs ${s.badgeMuted} ml-2 whitespace-nowrap`}>
                        Detected
                      </span>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>
      {detectedTimezone && value === detectedTimezone ? (
        <p className={`${s.hintDetected} text-xs font-body flex items-center gap-1`}>
          <Globe className="w-3 h-3" />
          {defaultWhenDetected}
        </p>
      ) : (
        <p className="text-[#9CA3AF]/70 text-xs font-body">{defaultOther}</p>
      )}
    </div>
  );
};

export default TimezoneField;
